import { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';

type PaymentItem = {
  id: string;
  amountRM: number;
  status: 'PENDING' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  createdAt: string;
  kolProfile: {
    id: string;
    name: string;
    email: string | null;
  };
  job: {
    id: string;
    title: string;
    brandOrg: { id: string; name: string };
  };
};

type PaymentsResponse = {
  items: PaymentItem[];
  summary: {
    totalCount: number;
    totalAmountRM: number;
    byStatus: Record<string, { count: number; amountRM: number }>;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
};

const statuses = ['all', 'PENDING', 'PAID', 'OVERDUE', 'CANCELLED'] as const;

export default function AdminPayments() {
  const [items, setItems] = useState<PaymentItem[]>([]);
  const [status, setStatus] = useState<(typeof statuses)[number]>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<PaymentsResponse | null>(null);

  useEffect(() => {
    let active = true;
    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await api.get<PaymentsResponse>('/admin/payments', {
          params: {
            status: status === 'all' ? undefined : status,
            search: search || undefined,
            page,
            limit: 20,
          },
        });
        if (!active) return;
        setResponse(data);
        setItems(data.items || []);
      } catch (err: any) {
        if (!active) return;
        setError(err.response?.data?.error || 'Failed to load payments');
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [status, search, page]);

  const summary = useMemo(() => {
    return (
      response?.summary || {
        totalCount: 0,
        totalAmountRM: 0,
        byStatus: {},
      }
    );
  }, [response]);

  const pagination = response?.pagination || {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  };

  const paymentStatusPill = (status: string) => {
    if (status === 'PAID') return 'status-pill status-pill-pos';
    if (status === 'PENDING') return 'status-pill status-pill-warn';
    if (status === 'OVERDUE') return 'status-pill status-pill-danger';
    return 'status-pill status-pill-muted';
  };

  return (
    <div className="space-y-6 text-glass-text">
      <div>
        <h1 className="page-title">Payment Management</h1>
        <p className="text-glass-text-2 text-sm mt-1">Platform-wide KOL payment records and status</p>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-500/10 border border-red-500/30 rounded-lg p-3">{error}</div>
      )}

      <div className="grid md:grid-cols-4 gap-4">
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Total Records</div>
          <div className="stat-display text-2xl mt-1">{summary.totalCount}</div>
        </div>
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Total Value (RM)</div>
          <div className="stat-display text-2xl mt-1 text-accent2">{summary.totalAmountRM.toLocaleString()}</div>
        </div>
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Pending</div>
          <div className="stat-display text-2xl mt-1 text-[rgb(var(--warn))]">{summary.byStatus.PENDING?.count || 0}</div>
        </div>
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Paid</div>
          <div className="stat-display text-2xl mt-1 text-[rgb(var(--pos))]">{summary.byStatus.PAID?.count || 0}</div>
        </div>
      </div>

      <div className="glass-card p-4 grid md:grid-cols-2 gap-4">
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
          placeholder="Search campaign/KOL"
          className="glass-input w-full px-4 py-2"
        />
        <select
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value as (typeof statuses)[number]);
          }}
          className="glass-input w-full px-4 py-2"
        >
          {statuses.map((s) => (
            <option key={s} value={s}>
              {s === 'all' ? 'All statuses' : s}
            </option>
          ))}
        </select>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-glass bg-glass-2/30">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Campaign</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Brand</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">KOL</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Amount (RM)</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-glass">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-glass-text-2">
                    Loading payments...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-glass-text-2">
                    No payments found.
                  </td>
                </tr>
              ) : (
                items.map((p) => (
                  <tr key={p.id} className="hover:bg-glass-3/40 transition-colors">
                    <td className="px-4 py-4 text-sm text-glass-text">{p.job?.title || '-'}</td>
                    <td className="px-4 py-4 text-sm text-glass-text-2">{p.job?.brandOrg?.name || '-'}</td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-glass-text">{p.kolProfile?.name || '-'}</div>
                      <div className="text-xs text-glass-text-3">{p.kolProfile?.email || 'No email'}</div>
                    </td>
                    <td className="px-4 py-4 text-sm font-semibold text-accent2">{p.amountRM.toLocaleString()}</td>
                    <td className="px-4 py-4">
                      <span className={paymentStatusPill(p.status)}>{p.status}</span>
                    </td>
                    <td className="px-4 py-4 text-sm text-glass-text-2">{new Date(p.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-glass flex items-center justify-between text-sm text-glass-text-2">
          <span>
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} payments)
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pagination.page <= 1}
              className="btn-glass px-3 py-1.5 text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={pagination.page >= pagination.totalPages}
              className="btn-glass px-3 py-1.5 text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
